import * as Yup from 'yup';
import {
  Alert,
  Box,
  Button,
  IconCheckmarkCircle,
  IconCircleDashed,
  IconExclamationCircle,
  IconMinusCircle,
  PageHeader,
  Select,
  Text,
  TextInput,
  Toggle,
} from '@tocktix/pantry';
import { BusinessForSwitcher } from '@tock/shared/components/business-switcher/BusinessSwitcherItem';
import { Form, Formik } from 'formik';
import {
  ResyVenueImportStatusQuery,
  useBusinessesForSwitcherQuery,
  useResyVenueImportStatusQuery,
  useStartResyVenueImportMutation,
} from 'generated/graphql';
import { gql } from '@apollo/client';
import BusinessDropdownSelector from 'components/BusinessDropdownSelector';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

gql`
  query ResyVenueImportStatus($workflowIdentifier: WorkflowIdentifierInput!) {
    resyVenueImportStatus(workflowIdentifier: $workflowIdentifier) {
      state
      settled
      stage
      stageDisplayName
      businessId
      businessName
      failureReason
      steps {
        stage
        state
        displayName
        durationMillis
      }
    }
  }

  mutation StartResyVenueImport(
    $venueId: Int!
    $resyOsEmail: String
    $resyOsPassword: String
    $businessId: Int
    $pullFromResyProduction: Boolean!
    $forceCreateNewBusiness: Boolean!
    $activateImmediately: Boolean!
    $resyUserCsv: String
  ) {
    startResyVenueImport(
      venueId: $venueId
      resyOsEmail: $resyOsEmail
      resyOsPassword: $resyOsPassword
      businessId: $businessId
      pullFromResyProduction: $pullFromResyProduction
      forceCreateNewBusiness: $forceCreateNewBusiness
      activateImmediately: $activateImmediately
      resyUserCsv: $resyUserCsv
    ) {
      workflowId
      runId
    }
  }
`;

const resyImporterSchema = Yup.object({
  venueId: Yup.number()
    .typeError('Venue id must be a number')
    .integer('Venue id must be a whole number')
    .positive('Venue id must be greater than 0')
    .required('Venue id is required'),
  businessId: Yup.number().integer().positive().nullable(),
  resyOsEmail: Yup.string()
    .email('Must be an email address')
    .required('A ResyOS email is required'),
  resyOsPassword: Yup.string().required('A ResyOS password is required'),
});

/**
 * Which Resy deployment a run reads from. Only where the venue is read changes; the import always
 * writes into whatever Tock environment is serving this page.
 */
type ResyImportEnvironment = 'STAGING' | 'PRODUCTION';

/**
 * Where the operator's ResyOS credentials live between imports.
 *
 * <p>The email persists, because it is the same account every time and retyping it is friction for
 * nothing. The password only lives as long as the tab: a failed import is restarted by submitting
 * the form again, and sessionStorage survives the reload that might involve, but nothing is written
 * to disk and closing the tab clears it. Neither is ever sent anywhere except this form's own
 * mutation, which exchanges the password for a token server-side and discards it.
 */
const EMAIL_STORAGE_KEY = 'resyImporter.resyOsEmail';
const PASSWORD_STORAGE_KEY = 'resyImporter.resyOsPassword';

function readStored(storage: Storage, key: string): string {
  try {
    return storage.getItem(key) ?? '';
  } catch {
    // Private browsing modes can refuse storage outright. Forgetting is survivable; failing is not.
    return '';
  }
}

function writeStored(storage: Storage, key: string, value: string): void {
  try {
    if (value) {
      storage.setItem(key, value);
    } else {
      storage.removeItem(key);
    }
  } catch {
    // As above: remembering is a convenience, never a requirement.
  }
}

interface ResyImporterFormValues {
  venueId: string;
  resyOsEmail: string;
  resyOsPassword: string;
  resyEnvironment: ResyImportEnvironment;
  businessId: number | null;
  forceCreateNewBusiness: boolean;
  activateImmediately: boolean;
  // The file verbatim. The server parses and validates it, so the browser only has to read it —
  // sending the text rather than parsed rows keeps one implementation of what the columns mean.
  resyUserCsv: string | null;
  resyUserCsvFileName: string | null;
}

const emptyState: ResyImporterFormValues = {
  venueId: '',
  resyOsEmail: readStored(localStorage, EMAIL_STORAGE_KEY),
  resyOsPassword: readStored(sessionStorage, PASSWORD_STORAGE_KEY),
  // Staging is the default so the destructive-by-accident path is never the one you fall into.
  resyEnvironment: 'STAGING',
  businessId: null,
  // Reusing the business a venue is already imported as is the non-duplicating default.
  forceCreateNewBusiness: false,
  // Leaving the business for account management to activate is what every import did before this.
  activateImmediately: false,
  // No staff file is the default, which imports the venue without giving anyone access to it.
  resyUserCsv: null,
  resyUserCsvFileName: null,
};

interface StartedImport {
  venueId: string;
  workflowId: string;
  runId: string;
  pullFromResyProduction: boolean;
  businessId: number | null;
}

/**
 * How often the started run is asked how it is getting on.
 *
 * Five seconds because that is the interval `ImportSubscriptionResolvers.importStatus` pushes the
 * data importer's status at, and an import measured in minutes gains nothing from being asked more
 * often than that.
 */
const STATUS_POLL_INTERVAL_MS = 5000;

type ImportStatus = NonNullable<ResyVenueImportStatusQuery['resyVenueImportStatus']>;

interface StatusPresentation {
  color: 'primary' | 'success' | 'warning' | 'danger';
  title: string;
  message: string;
}

/**
 * What the operator is told about the run, from Temporal's account of it and the run's account of
 * itself.
 *
 * Every state names the venue, so that a status read on its own still says which import it is
 * about. A stage the run could not be asked for is not treated as an error: the run's state is the
 * part that matters, and it comes from somewhere else.
 *
 * Which business the venue is going into is deliberately not in any of this wording — it is its own
 * line below, because the business is the thing an operator is checking for and it reads better
 * named once than repeated into five sentences.
 */
const presentStatus = (startedImport: StartedImport, status: ImportStatus): StatusPresentation => {
  const stoppedAt = status.stageDisplayName ? ` It stopped at: ${status.stageDisplayName}.` : '';
  const reason = status.failureReason ?? 'Temporal did not say why.';

  switch (status.state) {
    case 'RUNNING':
      return {
        color: 'primary',
        title: `Importing venue ${startedImport.venueId}`,
        message: status.stageDisplayName
          ? `${status.stageDisplayName}.`
          : 'The import is running. Which step it is on could not be read from the run.',
      };
    case 'COMPLETED':
      return {
        color: 'success',
        title: `Imported venue ${startedImport.venueId}`,
        message: status.businessId
          ? "The venue's configuration and schedule have been copied over."
          : "The import finished, but the run did not say which Tock business it created. Find it in Temporal or in the venue's business_content row.",
      };
    case 'FAILED':
      return {
        color: 'danger',
        title: `Could not import venue ${startedImport.venueId}`,
        message: reason + stoppedAt,
      };
    case 'TIMED_OUT':
      return {
        color: 'danger',
        title: `Import of venue ${startedImport.venueId} timed out`,
        message: `The run ran out of time before finishing.${stoppedAt}`,
      };
    case 'CANCELED':
    default:
      return {
        color: 'warning',
        title: `Import of venue ${startedImport.venueId} was stopped`,
        message: `The run was cancelled or terminated rather than finishing.${stoppedAt}`,
      };
  }
};

/**
 * Which Tock business the venue is going into, named as soon as the import has created it rather
 * than only once the whole import is done.
 *
 * The name is worth a line of its own because it is what an operator is actually checking: an
 * import that had to disambiguate a colliding name produced "Borgo 2", and a bare id says nothing
 * about whether the right venue came through. It is the persisted name, read from the business row
 * by the status resolver, not the name in the Resy payload.
 *
 * Renders the id alone when the name is not available, which covers a business that has since been
 * deleted and one whose row carries no name. Renders nothing at all until the import has a
 * business, which is the first few seconds of every run.
 */
const ImportedBusiness: React.FC<{ status: ImportStatus }> = ({ status }) => {
  if (!status.businessId) {
    return null;
  }
  return (
    <Box
      padding={[100, 0, 0]}
      display="flex"
      gap={100}
      alignItems="center"
      data-testid="resy-import-business"
    >
      <Text variant="bodySm" color="fgSubtle">
        {status.settled ? 'Imported into' : 'Importing into'}
      </Text>
      {status.businessName && (
        <Text variant="bodyStrong" data-testid="resy-import-business-name">
          {status.businessName}
        </Text>
      )}
      <Text variant="bodySm" color="fgSubtle" data-testid="resy-import-business-id">
        business {status.businessId}
      </Text>
    </Box>
  );
};

/**
 * How long a step took, in the units an operator thinks in.
 *
 * Import steps run from well under a second to several minutes, so no single unit reads well across
 * the range: sub-minute durations get one decimal place while they are small enough for it to mean
 * anything, and anything longer is worded in minutes and seconds.
 */
const formatDuration = (millis: number): string => {
  const seconds = millis / 1000;
  if (seconds < 10) {
    return `${seconds.toFixed(1)}s`;
  }
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  const wholeMinutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds - wholeMinutes * 60);
  return remainingSeconds === 0 ? `${wholeMinutes}m` : `${wholeMinutes}m ${remainingSeconds}s`;
};

/**
 * Whether the operator has asked the system for less animation.
 *
 * Read at the moment of scrolling rather than subscribed to, because the answer only matters for
 * the one call that uses it and a change of mind between imports is picked up by the next read
 * anyway. Guarded because jsdom provides no matchMedia, and a page that cannot ask should still
 * scroll rather than throw.
 */
const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * How each kind of step reads, with the words doubling as the icon's accessible name so that the
 * tick and the text never say different things.
 *
 * A skipped step gets a dash rather than a tick, and the same subdued colour as a step still to
 * come. A tick beside "Importing the venue's staff" reads as work done, which for an import given
 * no staff file it was not — and the dash has to be as clearly not a failure as it is not a
 * success, so the danger colour is out too.
 */
const STEP_PRESENTATION = {
  COMPLETED: { Icon: IconCheckmarkCircle, color: 'fgSuccessBase', label: 'done' },
  IN_PROGRESS: { Icon: IconCircleDashed, color: 'fgSubtle', label: 'in progress' },
  SKIPPED: { Icon: IconMinusCircle, color: 'fgSubtle', label: 'skipped' },
  STOPPED: { Icon: IconExclamationCircle, color: 'fgDangerBase', label: 'stopped here' },
} as const;

/**
 * The steps the import has taken, accumulating rather than replaced on each poll.
 *
 * A list rather than a single "currently doing X" line because the interesting question during an
 * import is usually not where it is now but what it has already got through and how long each part
 * took — and on a run that stopped, that history is nearly all of the diagnosis.
 *
 * The server decides which steps appear, how long each took, and which of them the run had no work
 * for. Nothing here sits pending forever, because a step the run passed over says so rather than
 * waiting; and the step in progress carries no duration, because timing it in the browser would
 * mean showing a number from a different clock to every other number in the list.
 */
const ImportChecklist: React.FC<{ steps: ImportStatus['steps'] }> = ({ steps }) => {
  if (steps.length === 0) {
    return null;
  }
  return (
    <Box
      padding={[200, 0, 0]}
      display="flex"
      flexDirection="column"
      gap={100}
      data-testid="resy-import-checklist"
    >
      {steps.map((step) => {
        const { Icon, color, label } = STEP_PRESENTATION[step.state];
        return (
          <Box
            key={step.stage}
            display="flex"
            gap={100}
            alignItems="center"
            data-testid={`resy-import-step-${step.stage}`}
          >
            <Icon size={100} color={color} titleAccess={label} />
            <Text variant="bodySm">{step.displayName}</Text>
            <Text
              variant="bodySm"
              color="fgSubtle"
              data-testid={`resy-import-step-${step.stage}-detail`}
            >
              {step.durationMillis === null || step.durationMillis === undefined
                ? label
                : formatDuration(step.durationMillis)}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
};

const ResyImporterRoute: React.FC = () => {
  const [startResyVenueImport] = useStartResyVenueImportMutation();
  const { data: businessesData, loading: businessesLoading } = useBusinessesForSwitcherQuery();
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessForSwitcher>();
  const [startedImport, setStartedImport] = useState<StartedImport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const staffCsvInputRef = useRef<HTMLInputElement>(null);
  const pageTopRef = useRef<HTMLDivElement>(null);
  const scrolledForWorkflowId = useRef<string | null>(null);
  const businesses = businessesData?.businesses ?? [];

  const {
    data: statusData,
    error: statusError,
    stopPolling,
  } = useResyVenueImportStatusQuery({
    variables: {
      workflowIdentifier: {
        workflowId: startedImport?.workflowId ?? '',
        runId: startedImport?.runId ?? '',
      },
    },
    skip: startedImport === null,
    pollInterval: STATUS_POLL_INTERVAL_MS,
    // The point of asking again is to see a run that has moved on, which a cached answer never has.
    fetchPolicy: 'network-only',
  });
  const status = statusData?.resyVenueImportStatus ?? null;

  useEffect(() => {
    if (status?.settled) {
      stopPolling();
    }
  }, [status?.settled, stopPolling]);

  /**
   * Returns to the top of the page when an import starts.
   *
   * The status lives in the column beside the form rather than below it, so it is already on
   * screen from the first poll onwards — but only for someone who is at the top. Submitting
   * happens at the bottom of a form taller than most screens, so without this the operator is
   * left looking at the button they just pressed while the run reports itself out of sight.
   *
   * Keyed on the workflow id rather than on the status appearing, because the status re-renders on
   * every poll and scrolling then would drag the page out from under whoever is reading it. A
   * second import is a second workflow id, so that one scrolls again.
   */
  useEffect(() => {
    const pageTop = pageTopRef.current;
    const workflowId = startedImport?.workflowId;
    if (!pageTop || !workflowId || scrolledForWorkflowId.current === workflowId) {
      return;
    }
    scrolledForWorkflowId.current = workflowId;
    pageTop.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [startedImport]);

  const onSubmit = async (values: ResyImporterFormValues) => {
    setStartedImport(null);
    setError(null);
    const pullFromResyProduction = values.resyEnvironment === 'PRODUCTION';
    // Remembered only once the operator has actually submitted them, so a half-typed address is
    // never what greets them next time.
    writeStored(localStorage, EMAIL_STORAGE_KEY, values.resyOsEmail.trim());
    writeStored(sessionStorage, PASSWORD_STORAGE_KEY, values.resyOsPassword);
    try {
      const { data } = await startResyVenueImport({
        variables: {
          venueId: Number(values.venueId),
          // The password goes no further than the mutation, which trades it for a token and drops
          // it. Nothing here is stored server-side or written into the workflow.
          resyOsEmail: values.resyOsEmail.trim(),
          resyOsPassword: values.resyOsPassword,
          businessId: values.businessId,
          pullFromResyProduction,
          forceCreateNewBusiness: values.forceCreateNewBusiness,
          activateImmediately: values.activateImmediately,
          resyUserCsv: values.resyUserCsv,
        },
      });
      const workflow = data?.startResyVenueImport;
      if (!workflow) {
        setError('The import did not start. No workflow was returned.');
        return;
      }
      setStartedImport({
        venueId: values.venueId,
        workflowId: workflow.workflowId,
        runId: workflow.runId,
        pullFromResyProduction,
        businessId: values.businessId,
      });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    }
  };

  return (
    <Box ref={pageTopRef} padding={[0, 500, 500]} data-testid="resy-importer-page">
      <PageHeader
        title="Resy Importer"
        description="Copies a Resy venue's configuration and schedule into a new or existing Tock business. The import runs in the background and can take a while."
      />
      <Box display="flex" gap={500} alignItems="flexStart">
        <Box padding={[300, 0]} style={{ flex: '0 1 480px', maxWidth: '480px' }}>
          <Formik<ResyImporterFormValues>
            initialValues={emptyState}
            validationSchema={resyImporterSchema}
            onSubmit={onSubmit}
          >
            {({
              values,
              errors,
              touched,
              dirty,
              isValid,
              isSubmitting,
              handleChange,
              handleBlur,
              setFieldValue,
            }) => (
              <Form>
                <Box display="flex" flexDirection="column" gap={300}>
                  <TextInput
                    id="venueId"
                    name="venueId"
                    label="Resy venue id"
                    className="ResyImporter-textInput"
                    data-testid="resy-importer-venue-id-input"
                    value={values.venueId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    validationStatus={touched.venueId && errors.venueId ? 'error' : undefined}
                    validationMessage={touched.venueId ? errors.venueId : undefined}
                    fullWidth
                  />
                  <Box style={{ width: '100%' }}>
                    <BusinessDropdownSelector
                      businesses={businesses}
                      isDisabled={isSubmitting || businessesLoading}
                      label="Optional existing Tock business"
                      layout="stacked"
                      selectedBusiness={selectedBusiness}
                      onSelectBusiness={(business) => {
                        setSelectedBusiness(business);
                        setFieldValue('businessId', business.id);
                        setFieldValue('forceCreateNewBusiness', false);
                      }}
                    />
                    {selectedBusiness && (
                      <Button
                        label="Clear existing business"
                        type="button"
                        data-testid="resy-importer-clear-business-button"
                        onClick={() => {
                          setSelectedBusiness(undefined);
                          setFieldValue('businessId', null);
                        }}
                      />
                    )}
                  </Box>
                  <Select
                    id="resyEnvironment"
                    name="resyEnvironment"
                    label="Resy environment"
                    hint="Which Resy the venue is read from. The business is created in whichever Tock environment you are pointed at either way."
                    data-testid="resy-importer-environment-select"
                    value={values.resyEnvironment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                  >
                    <Select.Option value="STAGING">Staging</Select.Option>
                    <Select.Option value="PRODUCTION">Production</Select.Option>
                  </Select>
                  <TextInput
                    id="resyOsEmail"
                    name="resyOsEmail"
                    label="ResyOS email"
                    className="ResyImporter-textInput"
                    type="email"
                    autoComplete="username"
                    data-testid="resy-importer-resyos-email-input"
                    value={values.resyOsEmail}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    validationStatus={
                      touched.resyOsEmail && errors.resyOsEmail ? 'error' : undefined
                    }
                    validationMessage={touched.resyOsEmail ? errors.resyOsEmail : undefined}
                    fullWidth
                  />
                  <TextInput
                    id="resyOsPassword"
                    name="resyOsPassword"
                    label="ResyOS password"
                    className="ResyImporter-textInput"
                    type="password"
                    autoComplete="current-password"
                    data-testid="resy-importer-resyos-password-input"
                    value={values.resyOsPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    validationStatus={
                      touched.resyOsPassword && errors.resyOsPassword ? 'error' : undefined
                    }
                    validationMessage={touched.resyOsPassword ? errors.resyOsPassword : undefined}
                    fullWidth
                  />
                  {values.resyEnvironment === 'PRODUCTION' && (
                    <Box data-testid="resy-importer-production-warning">
                      <Alert
                        color="warning"
                        title="Reading from Resy production"
                        message="The credentials must be a production ResyOS account, and venue ids differ between the two environments. This only changes where the venue is read from — the business is still created in whichever Tock environment you are pointed at."
                      />
                    </Box>
                  )}
                  <Toggle
                    id="forceCreateNewBusiness"
                    name="forceCreateNewBusiness"
                    label="Force a new business"
                    description="Off reuses the business this venue is already imported as."
                    data-testid="resy-importer-force-new-business-toggle"
                    checked={values.forceCreateNewBusiness}
                    disabled={values.businessId !== null}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {values.forceCreateNewBusiness && (
                    <Box data-testid="resy-importer-force-new-business-warning">
                      <Alert
                        color="warning"
                        title="Importing the venue a second time"
                        message="A venue that is already imported will be imported again as a separate business, so it exists twice in Tock. The new one takes a numbered name and domain to avoid colliding with the first. Nothing merges them afterwards, and this tool cannot undo it — backing out a mistaken import means deleting its business_content row and its business by hand."
                      />
                    </Box>
                  )}
                  <Toggle
                    id="activateImmediately"
                    name="activateImmediately"
                    label="Activate the business immediately"
                    description="Off leaves it for account management to activate."
                    data-testid="resy-importer-activate-immediately-toggle"
                    checked={values.activateImmediately}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {values.activateImmediately && (
                    <Box data-testid="resy-importer-activate-immediately-warning">
                      <Alert
                        color="warning"
                        title="Activate without review"
                        message="Activate the business and allow availability to be published immediately after importing.
                        Not recommended for use in PROD as the import does not configure a subscription plan / billing method.
                        Availability will not be published until enabled in dashboard."
                      />
                    </Box>
                  )}
                  <Box display="flex" flexDirection="column" gap={100}>
                    <label htmlFor="resyUserCsv">Venue staff CSV</label>
                    <input
                      id="resyUserCsv"
                      ref={staffCsvInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      data-testid="resy-importer-staff-csv-input"
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const file = event.target.files?.[0];
                        if (!file) {
                          return;
                        }
                        setError(null);
                        // FileReader rather than the shorter file.text(), because Blob.text is
                        // missing from the jsdom the tests run in and this reads the same everywhere.
                        const reader = new FileReader();
                        reader.onload = () => {
                          setFieldValue('resyUserCsv', String(reader.result));
                          setFieldValue('resyUserCsvFileName', file.name);
                        };
                        reader.onerror = () => setError(`Could not read ${file.name}.`);
                        reader.readAsText(file);
                      }}
                    />
                    {values.resyUserCsvFileName && (
                      <Box data-testid="resy-importer-staff-csv-name">
                        <Button
                          label={`Import the venue without ${values.resyUserCsvFileName}`}
                          type="button"
                          data-testid="resy-importer-clear-staff-csv-button"
                          onClick={() => {
                            // The input holds the file independently of Formik, so clearing only the
                            // form values would leave the field naming a file that is not being sent.
                            if (staffCsvInputRef.current) {
                              staffCsvInputRef.current.value = '';
                            }
                            setFieldValue('resyUserCsv', null);
                            setFieldValue('resyUserCsvFileName', null);
                          }}
                        />
                      </Box>
                    )}
                    <Text variant="bodySm" color="fgSubtle">
                      Optional, and one file per venue. Columns: name, email, hashed_password. Each
                      person becomes a Tock patron with the employee role on the business and signs
                      in with the Resy password they already have. An address that is already a Tock
                      account keeps that account and its password, and is only given the role.
                    </Text>
                  </Box>
                  <Box>
                    <Button
                      label={isSubmitting ? 'Starting import...' : 'Start import'}
                      type="submit"
                      data-testid="start-resy-import-button"
                      disabled={!dirty || !isValid || isSubmitting}
                    />
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
        {/* Beside the form rather than below it, and sticky, so that a run reports itself
            somewhere the operator can watch without scrolling away from what they submitted. */}
        <Box
          padding={[300, 0]}
          data-testid="resy-importer-status-column"
          style={{
            flex: '1 1 0',
            minWidth: 0,
            position: 'sticky',
            top: 'var(--pantry-space-300)',
          }}
        >
          {startedImport && (
            <Box data-testid="resy-import-status-section">
              {status && (
                <Box padding={[0, 0, 300]} data-testid="resy-import-status">
                  <Alert {...presentStatus(startedImport, status)} />
                  <ImportedBusiness status={status} />
                  <ImportChecklist steps={status.steps} />
                </Box>
              )}
              {statusError && (
                <Box padding={[0, 0, 300]} data-testid="resy-import-status-unavailable">
                  <Alert
                    color="warning"
                    title="Cannot tell how the import is getting on"
                    message={`${statusError.message} The import itself is unaffected — follow it in Temporal under workflow ${startedImport.workflowId}.`}
                  />
                </Box>
              )}
              <Box padding={[0, 0, 300]} data-testid="resy-import-workflow">
                <Text variant="bodySm" color="fgSubtle">
                  Temporal workflow {startedImport.workflowId}, run {startedImport.runId}, on the
                  imports queue.
                </Text>
              </Box>
            </Box>
          )}
          {error && (
            <Box padding={[0, 0, 300]} data-testid="resy-import-error">
              <Alert color="danger" title="Could not start the import" message={error} />
            </Box>
          )}
        </Box>
      </Box>
      <Text variant="bodySm" color="fgSubtle">
        Only one import can run per venue, and a venue that already imported cleanly cannot be
        imported again.
      </Text>
    </Box>
  );
};

export default ResyImporterRoute;
